import {
    Component,
    forwardRef,
    OnDestroy,
    OnInit,
    OnChanges,
    SimpleChanges,
    Renderer2,
    EventEmitter,
    Input,
    Output,
    HostBinding,
    ViewContainerRef,
    ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { OverlayRef, CdkOverlayOrigin, Overlay, OverlayConfig } from  '@angular/cdk/overlay';
import { TemplatePortalDirective } from  '@angular/cdk/portal';

import { Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { OptionsInterface } from '../options.interface';
import { NmCascadeBottomSheetComponent } from '../cascade-bottom-sheet/cascade-bottom-sheet.component';

let NEXT_ID = 0;

@Component({
    selector: 'nm-cascade-dropdownlist',
    templateUrl: './cascade-list.component.html',
    styleUrls: ['./cascade-list.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => NmCascadeListComponent),
        multi: true
    }],
})
export class NmCascadeListComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor {

    /* ç»ä»¶ id */
    @HostBinding('attr.id')
    @Input()
    public id = `nm-cascade-list-${NEXT_ID++}`;

    overlayRef: OverlayRef;
    @ViewChild(CdkOverlayOrigin, {static: true}) overlayOrigin: CdkOverlayOrigin;

    @ViewChild(TemplatePortalDirective, {static: true}) cascadeMenuTemplate: TemplatePortalDirective;

    @Input() disabled = false;
    @Input() placeholder = 'è¯·éæ©';
    @Input() appearance: 'legacy' | 'standard' | 'fill' | 'outline' = 'legacy';
    @Input() floatLabel: 'never' | 'always' | 'auto' = 'auto';
    @Input() label: string;

    // Whether the cascade dropdownlist UI is in touch mode.
    @Input()
    get touchUi(): boolean { return this._touchUi; }
    set touchUi(value: boolean) {
        this._touchUi = coerceBooleanProperty(value);
    }
    private _touchUi = false;

    // bottomSheet
    bottomSheetRef: MatBottomSheetRef;

    // data
    @Input() options: Array<OptionsInterface>;
    @Input() clearable = false;
    @Input() fullLevels = true;
    @Input() changeOnSelect = false;

    // bind value
    @Input() model: Array<string>;
    @Output() modelChange: EventEmitter<any> = new EventEmitter<any>();
    @Output() clear: EventEmitter<boolean> = new EventEmitter<boolean>();

    private ngUnsubscribe: Subject<any>;

    steps: Array<Array<OptionsInterface>> = [];
    menuVisible = false;
    inputHover = false;
    currentLabels: Array<OptionsInterface> = [];
    globalListenFunc: Function;
    inputValue: string;

    constructor(
        private renderer: Renderer2,
        private breakpointObserver: BreakpointObserver,
        private bottomSheet: MatBottomSheet,
        private overlay: Overlay,
        private viewContainerRef: ViewContainerRef
    ) {
        this.ngUnsubscribe = new Subject();
    }

    private controlChange: Function = () => { };
    private controlTouch: Function = () => { };

    private setInputValue() {
        if (this.fullLevels) {
            this.currentLabels.map((value, i) => {
                this.inputValue += `${value.label}${i < this.currentLabels.length - 1 ? ' / ' : ''}`;
            });
        } else {
            this.inputValue = this.currentLabels[this.currentLabels.length - 1].label;
        }
    }

    ngOnInit() {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.options) {
            this.options = changes.options.currentValue;
            this.init();
        } else if (changes.model) {
            this.model = changes.model.currentValue;
            this.init();
        }
    }

    init(): void {
        this.reset();

        if (this.model && this.model.length) {
            const getLabel = (options: Array<OptionsInterface>, val: string) => {
                const item: OptionsInterface = options.filter((item: OptionsInterface) => item.value === val)[0];
                return { children: item.children, val: item };
            };

            let options: Array<OptionsInterface> = this.options;
            const val: Array<OptionsInterface> = this.model.map(v => {
                const { children, val } = getLabel(options, v);
                options = children;
                return val;
            });

            this.currentLabels = val.filter(v => !!v);
            this.setInputValue();
            this.initSteps(0, this.options);
        }

        this.breakpointObserver.observe(Breakpoints.Handset).pipe(
            takeUntil(this.ngUnsubscribe),
            map(match => match.matches)
        ).subscribe(matches => {
            this.touchUi !== matches && !matches && this.bottomSheetRef && this.bottomSheetRef.dismiss();
        });
    }

    writeValue(value: any): void {
        this.model = value;
    }

    registerOnChange(fn: Function): void {
        this.controlChange = fn;
    }

    registerOnTouched(fn: Function): void {
        this.controlTouch = fn;
    }

    clickHandle(event: MouseEvent): void {
        event.stopPropagation();
        if (this.disabled) {
            return;
        }
        const element: HTMLElement = event.target as HTMLElement;
        const isSelfTrigger = ['SPAN', 'I', 'INPUT'].find(v => v === element.tagName);
        if (!isSelfTrigger) {
            return;
        }

        if (this.touchUi) {
            this.closeCascadeMenu();
            this.openBottomSheet();
        } else {
            this.openCascadeMenu();
        }
    }

    changeLabels(): void {
        this.inputValue = '';
        const nextValue: Array<OptionsInterface> = [];

        this.steps.forEach((items: Array<OptionsInterface>) => {
            const steps: Array<OptionsInterface> = items.filter((item: OptionsInterface) => item.active);
            nextValue.push(steps[0]);
        });
        this.currentLabels = nextValue;
        this.setInputValue();
        const next = nextValue.map((item: OptionsInterface) => item.value);
        this.model = next;
        this.modelChange.emit(next);
        this.controlChange(next);
    }

    clearValue(event?: Event): void {
        event && event.stopPropagation();
        this.model = [];
        this.reset();
        this.clear.emit(true);
        this.modelChange.emit(this.model);
    }

    reset(): void {
        this.steps = [];

        this.currentLabels = [];
        if ((this.model && !this.model.length) || !this.model) {
            const step = this.options.map((option: OptionsInterface) => ({
                ...option,
                active: false
            }));
            this.steps = [step];
        }

        this.closeCascadeMenu();
        this.inputValue = '';
    }

    initSteps(index, options) {
        const step: Array<OptionsInterface> = [];
        options.map((item: OptionsInterface) => {
            if (this.model[index] === item.value) {
                options = item.children;
                step.push(({
                    ...item,
                    active: true
                }));
            } else {
                step.push(item);
            }
        });
        this.steps.push(step);
        index += 1;
        index < this.model.length && this.initSteps(index, options);
    }

    selectHandle({step, index}: {step: number, index: number}): any {
        if (this.steps[step][index].disabled) {
            return;
        }

        this.steps[step] = this.steps[step].map((item: OptionsInterface, i: number) =>
            ({
                ...item,
                active: i === index
            }));
        // reset steps
        this.steps.length = step + 1;
        const next = this.steps[step][index].children;

        // go next
        if (next && Array.isArray(next) && next.length) {
            // change on select (props)
            this.changeOnSelect && this.changeLabels();
            const nativeNext = next.map((item: OptionsInterface) => ({
                ...item,
                active: false
            }));
            return this.steps.push(nativeNext);
        }

        // last step
        this.changeLabels();
        this.closeCascadeMenu();
        this.touchUi && this.bottomSheetRef.dismiss();
    }

    showClearIcon(): boolean {
        return !!(this.clearable && this.inputHover && this.currentLabels.length);
    }

    openCascadeMenu(): void {
        this.menuVisible = true;
        this.overlayRef = this.overlay.create(this.getOverlayConfig());
        this.overlayRef.attach(this.cascadeMenuTemplate);
        this.overlayRef.backdropClick().subscribe(() => this.closeCascadeMenu());
    }

    closeCascadeMenu(): void {
        this.overlayRef && this.overlayRef.detach();
        this.menuVisible = false;
    }

    openBottomSheet(): void {
        this.bottomSheetRef = this.bottomSheet.open(NmCascadeBottomSheetComponent, {
            data: this
        });
    }

    private getOverlayConfig(): OverlayConfig {
        const positionStrategy = this.overlay.position()
            .flexibleConnectedTo(this.overlayOrigin.elementRef)
            .withPush(false)
            .withPositions([{
                originX: 'start',
                originY: 'bottom',
                overlayX: 'start',
                overlayY: 'top'
            },
                {
                    originX: 'start',
                    originY: 'top',
                    overlayX: 'start',
                    overlayY: 'bottom'
                }]);

        const scrollStrategy = this.overlay.scrollStrategies.reposition();

        return new OverlayConfig({
            positionStrategy,
            scrollStrategy,
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-transparent-backdrop'
        });
    }

    ngOnDestroy(): void {
        this.globalListenFunc && this.globalListenFunc();
        // unsubscribe
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}
